import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Warning, CalendarToday } from '@mui/icons-material';
import { tokens } from '../../theme';
import { learnerService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmDialog from '../common/ConfirmDialog';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { formatDate } from '../../utils/dateFormatter';

const EnrollmentButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  borderRadius: tokens.radius.sm,
}));

const WarningText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5)
}));

const CourseEnrollment = ({ 
  course, 
  instance, 
  enrolled,
  enrolledObj,
  progress,
  activities,
  prerequisites,
  showEnrollCount,
  enrollmentCount,
  showEnrollBtn,
  approvalStatus,
  refreshOnComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(approvalStatus?.status === '0');
  const { showToast } = useToast();
  const { openContentPlayer } = useContentPlayer();
  // State for confirm dialogs
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
  const [switchInstanceDialogOpen, setSwitchInstanceDialogOpen] = useState(false);

  // Check if all required prerequisites are completed
  const requiredPrerequisites = prerequisites?.filter(p => p.required === '1') || [];
  const allRequiredCompleted = !requiredPrerequisites.length || 
    requiredPrerequisites.every(p => p.status === '1');
  
  // Check if enrollment deadline has passed
  const enrollDeadlinePassed = instance?.enrollemnt_deadline && 
    new Date(instance.enrollemnt_deadline) <= new Date();

  const startCourse = async (courseId) => {
    try {
      setLoading(true);
      const response = await learnerService.startCourse(courseId);
      if (response.data.success) {
        // Redirect to course content player
        if (response.data.firstModuleId) {
          openContentPlayer(
            courseId, 
            response.data.firstModuleId, 
            `/courses/${courseId}`, 
            refreshOnComplete, 
            true, // isLearnerMode
            response.data.clrId, // content learner reference ID if available
            null, //Lpath passing its for learning path module
            refreshOnComplete
          );
        } else {
          showToast('No content available for this course','error');
        }
      } else {
        showToast(response.data.error || 'Failed to start course','error');
      }
    } catch (error) {
      console.error('Error starting course:', error);
      showToast('Failed to start course','error');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId, instanceId) => {
    try {
      setLoading(true);
      const response = await learnerService.enrollInCourse(courseId, instanceId);
      if (response.data.success) {
        showToast('Successfully enrolled in the course','success');
        if (refreshOnComplete) {
          refreshOnComplete();
        } else {
          window.location.reload();
        }
      } else {
        showToast(response.data.message || 'Failed to enroll in course','error');
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
      showToast(error.response?.data?.message || 'Failed to enroll in course','error');
      setLoading(false);
    }
  };

  const handleInstanceSwitch = async (courseId, instanceId) => {
    try {
      setLoading(true);
      const response = await learnerService.switchInstance(courseId, instanceId);
      if (response.data.success) {
        showToast('Successfully switched instance','success');
        if (refreshOnComplete) {
          refreshOnComplete();
        } else {
          window.location.reload();
        }
      } else {
        showToast(response.data.message || 'Failed to switch instance','error');
      }
    } catch (error) {
      console.error('Error switching instance:', error);
      showToast('Failed to switch instance','error');
    } finally {
      setLoading(false);
      setSwitchInstanceDialogOpen(false);
    }
  };

  const requestEnrollment = async (courseId, instanceId) => {
    try {
      setLoading(true);
      const response = await learnerService.enrollmentRequest(courseId, instanceId);
      
      if (response.data.success) {
        setPendingRequest(true);
        showToast('Enrollment request sent to manager','success');
      } else {
        showToast(response.data.error || 'Failed to send enrollment request','error');
      }
    } catch (error) {
      console.error('Error requesting enrollment:', error);
      showToast('Failed to send enrollment request','error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async (courseId, instanceId) => {
    try {
      setLoading(true);
      const response = await learnerService.selfUnenroll(courseId, instanceId);
      if (response.data.success) {
        showToast('Successfully unenrolled from the course','success');
        if (refreshOnComplete) {
          refreshOnComplete();
        } else {
          window.location.reload();
        }
      } else {
        showToast(response.data.message || 'Failed to unenroll from course','error');
      }
    } catch (error) {
      console.error('Error unenrolling from course:', error);
      showToast('Failed to unenroll from course','error');
      setLoading(false);
      setUnenrollDialogOpen(false);
    }
  };

  // Determine button text based on progress
  const getButtonText = () => {
    if (!progress) return 'Start';
    if (progress.completed === 0) return 'Start';
    if (progress.completed >= (activities?.length || 0)) return 'Revisit';
    return 'Continue';
  };

  //const canSelfUnenroll = [true, 1, '1', 'true'].includes(course.self_unenrollment);
  const canSelfUnenroll = !course.due_date ? true : false;

  const getDaysRemaining = (dateString) => {
    if (!dateString) return null;

    const today = new Date();
    const dueDate = new Date(dateString);
    const difference = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    return difference >= 0 ? `${difference} days left` : 'Overdue';
  };

  // Render enrollment count for admin/manager roles
  if (showEnrollCount) {
    return (
      <Box>
        <EnrollmentButton variant="outlined" disabled>
          {enrollmentCount} Enrollment(s)
        </EnrollmentButton>
      </Box>
    );
  }
  // Render start/continue button for enrolled users
  if (enrolled) {
    const buttonText = getButtonText();
    const disableStart = requiredPrerequisites.length > 0 && 
      requiredPrerequisites.filter(p => p.status === '1').length < requiredPrerequisites.length;

    return (
      <Box>
        <EnrollmentButton
          variant="contained"
          sx={{ backgroundColor: progress?.completed >= (activities?.length || 0) ? tokens.color.brand : '', color: progress?.completed >= (activities?.length || 0) ? tokens.color.textPrimary : '', '&:hover':  {backgroundColor: progress?.completed >= (activities?.length || 0) ? tokens.color.brandLight : ''}}}
          //color="primary"
          disabled={disableStart || loading}
          onClick={() => startCourse(course.id)}
        >
          {loading ? <CircularProgress size={24} /> : buttonText}
        </EnrollmentButton>

        {course?.due_date && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 38,
              mb: 2,
              px: 1.5,
              border: `1px solid ${tokens.color.borderSecondary}`,
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.color.bgSecondary,
              color: tokens.color.textSecondary,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.textPrimary }}>
              Due {formatDate(course.due_date)}
            </Typography>

            <Typography variant="body2" sx={{ color: tokens.color.textTertiary }}>
              · {getDaysRemaining(course.due_date)}
            </Typography>
          </Box>
        )}

        {disableStart && (
          <WarningText>
            <Warning fontSize="small" color="warning" /> 
            Please complete all required prerequisites!
          </WarningText>
        )}

        {canSelfUnenroll && (
          <Link 
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 20,
              mb: 2,
              px: 1.5,
              cursor:'pointer',
            }}
            
            onClick={() => setUnenrollDialogOpen(true)}
          >
            Unenroll from this course
          </Link>
        )}
        {/* Unenroll Confirmation Dialog */}
         <ConfirmDialog
          open={unenrollDialogOpen}
          title="Confirm Unenrollment"
          content="Are you sure you want to unenroll from this course? This action cannot be undone."
          onConfirm={() => handleUnenroll(course.id, instance.id)}
          onCancel={() => setUnenrollDialogOpen(false)}
        />

      </Box>
    );
  }

  // Render enrollment options for non-enrolled users
  if (enrollDeadlinePassed) {
    return (
      <Box>
        <EnrollmentButton variant="outlined" disabled>
          Enrollment Closed
        </EnrollmentButton>
        <WarningText>
          Enrollment deadline has passed.
        </WarningText>
      </Box>
    );
  }

  if (requiredPrerequisites.length > 0 && !allRequiredCompleted) {
    return (
      <Box>
        <EnrollmentButton variant="outlined" disabled>
          Enroll
        </EnrollmentButton>
        <Box display="flex" alignItems="center" color="warning.main">
      <Warning fontSize="small" sx={{ mr: 1 }} />
      <Typography variant="body2">
        Please complete all required prerequisites!
      </Typography>
    </Box>
      </Box>
    );
  }

  // Handle different enrollment types
  if (course.enrollment_type === 'manager_approved' && !showEnrollBtn) {
    if (pendingRequest) {
      return (
        <Box>
          <EnrollmentButton variant="outlined" disabled>
            Request Pending
          </EnrollmentButton>
          <WarningText>
            Your enrollment request is awaiting manager approval.
          </WarningText>
        </Box>
      );
    }

    return (
      <Box>
        <EnrollmentButton 
          variant="contained" 
          color="primary"
          disabled={loading}
          onClick={() => requestEnrollment(course.id, instance?.id)}
        >
          {loading ? <CircularProgress size={24} /> : 'Enroll'}
        </EnrollmentButton>
        <WarningText>
          Manager's approval required. 'Enroll' to request approval.
        </WarningText>
      </Box>
    );
  }

  // Default: self-enrollment
  if (instance?.id !== enrolledObj?.instance_id && enrolledObj?.instance_id && !showEnrollBtn) {
    return (
      <Box>
        <EnrollmentButton 
          variant="contained" 
          color="primary"
          disabled={loading}
          onClick={() => setSwitchInstanceDialogOpen(true)}
        >
          {loading ? <CircularProgress size={24} /> : 'Switch Instance'}
        </EnrollmentButton>

        {/* Switch Instance Confirmation Dialog */}
        <ConfirmDialog
          open={switchInstanceDialogOpen}
          title="Confirm Instance Switch"
          content="Your enrollment and progress will be transferred to this Instance. Do you want to continue?"
          onConfirm={() => handleInstanceSwitch(course.id, instance.id)}
          onCancel={() => setSwitchInstanceDialogOpen(false)}
        />
      </Box>
    );
  }

  if (!showEnrollBtn) {
    return (
      <Box>
        <EnrollmentButton 
          variant="contained" 
          color="primary"
          disabled={loading}
          onClick={() => handleEnroll(course.id, instance?.id || '')}
        >
          {loading ? <CircularProgress size={24} /> : 'Enroll'}
        </EnrollmentButton>
      </Box>
    );
  }
  return null;
};

export default CourseEnrollment;